import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { $fetchApi } from '~/composables/fetchApi'
import { useNuxtApp, useRuntimeConfig } from '#imports'
import type {
    VehicleBrand,
    VehicleModel,
    VehicleVersion,
    DropdownOption
} from '~/interfaces/vehicles'
import type { UserVehicle } from '#cmui/types/vehicle'
import { VehicleManagementView } from '~/interfaces/vehicles'

const useVehiclesStore = defineStore('vehicles', () => {
    const brands = ref<VehicleBrand[]>([])
    const models = ref<VehicleModel[]>([])
    const versions = ref<VehicleVersion[]>([])
    const vehicleForDeletion = ref<UserVehicle | null>(null)
    const vehicleForModification = ref<UserVehicle | null>(null)

    const meta = ref({
        vehicleManagementView:
            VehicleManagementView.LIST as VehicleManagementView,
        isModificationForm: false,
        isLoading: false,
        isError: false
    })

    const brandsOptions = computed<DropdownOption[]>(() =>
        brands.value.map((brand) => ({
            id: brand.id,
            value: brand.name
        }))
    )

    const modelsOptions = computed<DropdownOption[]>(() =>
        models.value.map((model) => ({
            id: model.id,
            value: model.name
        }))
    )

    const versionsOptions = computed<DropdownOption[]>(() =>
        versions.value.map((version) => ({
            id: version.id,
            value: version.name
        }))
    )

    const isVehicleListView = computed(
        () => meta.value.vehicleManagementView === VehicleManagementView.LIST
    )

    const isVehicleDeletionView = computed(
        () => meta.value.vehicleManagementView === VehicleManagementView.DELETE
    )

    const isVehicleFormView = computed(
        () => meta.value.vehicleManagementView === VehicleManagementView.FORM
    )

    const vehicleToDeleteBrand = computed(
        () => vehicleForDeletion.value?.brandName || ''
    )

    const vehicleToDeleteModel = computed(
        () => vehicleForDeletion.value?.modelName || ''
    )

    async function fetchVehicleBrands() {
        const { $throwError } = useNuxtApp()
        meta.value.isLoading = true

        try {
            const data: any = await $fetchApi(
                `${useRuntimeConfig().public.APPLICATION_URL}/vehicly/brands`
            )
            brands.value = data.content
        } catch (e: any) {
            meta.value.isError = true
            $throwError(e, 'ResourceNotFound: fetchVehicleBrands')
        } finally {
            meta.value.isLoading = false
        }
    }

    async function fetchVehicleModels(brandId: number) {
        const { $throwError } = useNuxtApp()
        meta.value.isLoading = true

        try {
            const data: any = await $fetchApi(
                `${useRuntimeConfig().public.APPLICATION_URL}/vehicly/brands/${brandId}/models`
            )
            models.value = data.content
        } catch (e: any) {
            meta.value.isError = true
            $throwError(e, 'ResourceNotFound: fetchVehicleModels')
        } finally {
            meta.value.isLoading = false
        }
    }

    async function fetchVehicleVersions(modelId: number) {
        const { $throwError } = useNuxtApp()
        meta.value.isLoading = true

        try {
            const data: any = await $fetchApi(
                `${useRuntimeConfig().public.APPLICATION_URL}/vehicly/models/${modelId}/versions`
            )
            versions.value = data.content
        } catch (e: any) {
            meta.value.isError = true
            $throwError(e, 'ResourceNotFound: fetchVehicleVersions')
        } finally {
            meta.value.isLoading = false
        }
    }

    async function selectBrand(brandId: number) {
        versions.value = []
        models.value = []
        await fetchVehicleModels(brandId)
    }

    async function selectModel(modelId: number) {
        versions.value = []
        await fetchVehicleVersions(modelId)
    }

    function setVehicleManagementView(view: VehicleManagementView) {
        meta.value.vehicleManagementView = view
    }

    return {
        brands,
        models,
        versions,
        vehicleForDeletion,
        vehicleForModification,
        meta,
        brandsOptions,
        modelsOptions,
        versionsOptions,
        isVehicleListView,
        isVehicleDeletionView,
        isVehicleFormView,
        vehicleToDeleteBrand,
        vehicleToDeleteModel,
        fetchVehicleBrands,
        fetchVehicleModels,
        fetchVehicleVersions,
        selectBrand,
        selectModel,
        setVehicleManagementView
    }
})

type VehiclesStore = Omit<
    ReturnType<typeof useVehiclesStore>,
    keyof ReturnType<typeof defineStore>
>

export default useVehiclesStore
export type { VehiclesStore }
